import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import useShopify from '@cohort/wallet/hooks/useShopify';
import useStoreContext from '@cohort/wallet/hooks/useStoreContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {getStoreCheckoutRoute} from '@cohort/wallet/lib/Pages';
import {Colors} from '@cohort/wallet/lib/Theme';
import type {CampaignStoreWDto} from '@cohort/wallet-schemas/campaign';
import {ShoppingBag} from '@phosphor-icons/react';
import Color from 'color';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

const BuyButton: React.FC = () => {
  const {campaign, setTransitioning, setBuyingStep} = useStoreContext();
  const user = useUserStore(store => store.user);
  const {t} = useTranslation('components', {keyPrefix: 'store.buyButton'});
  const {hasDarkAccent} = useThemeContext();
  const navigate = useNavigate();
  const shopify = useShopify();
  const location = useLocation();

  const onBuyClick = useCallback(
    (campaign: CampaignStoreWDto) => {
      if (shopify.active) {
        if (user) {
          navigate({
            pathname: getStoreCheckoutRoute(campaign.store.slug),
            search: location.search, // Keep query params for redeemCodes when user is not logged in.
          });
        } else {
          shopify.signInCheckoutRedirect(campaign.store.slug, location.search);
        }
      } else {
        if (campaign.merchant.redirectLoginUrl === null) {
          setBuyingStep({
            total: user === null ? 3 : 2,
            current: 1,
          });
        }
        navigate({
          pathname: getStoreCheckoutRoute(campaign.store.slug),
          search: location.search, // Keep query params for redeemCodes when user is not logged in.
        });
      }
    },
    [location.search, navigate, setBuyingStep, shopify, user]
  );

  const disableBuyButton =
    (campaign.availableSupply !== null && campaign.availableSupply < 1) ||
    campaign.status === 'ended';

  const buyButtonText = (): string => {
    if (disableBuyButton) {
      return t('nftUnavailable');
    }
    if (campaign.store.pricing === 'free') {
      return t('get');
    }
    return t('buy');
  };

  return (
    <PrimaryButton
      testId={campaign.store.pricing === 'paid' ? 'buy' : 'claim'}
      size="large"
      onClick={() => {
        onBuyClick(campaign);
        setTransitioning(true);
      }}
      text={buyButtonText()}
      className="w-full"
      disabled={disableBuyButton}
      icon={
        disableBuyButton ? undefined : (
          <ShoppingBag
            className={classNames('h-5 w-5', campaign.status !== 'published' && 'opacity-50')}
            aria-hidden="true"
            style={{
              color: String(Color(hasDarkAccent ? Colors.White : Colors.Black)),
            }}
          />
        )
      }
      tracking={{
        namespace: 'store.buy',
        metadata: {type: campaign.store.pricing === 'paid' ? 'buy' : 'claim'},
      }}
    />
  );
};

export default BuyButton;
